/**
 * What an entry bills at.
 *
 * This is in `shared` for the same reason `bookingRefusal()` is: four write paths
 * reach the timesheet - the day form, the weekly grid, the bulk fill and the CSV
 * import - and each of them has to arrive at the same figure. A rate computed two
 * ways is an invoice that disagrees with the screen it was raised from, and that
 * is a worse bug than any of the ones this file's neighbours prevent, because
 * money leaves the building on the strength of it.
 *
 * Ported from the previous system's RateService, deliberately keeping its
 * precedence and its arithmetic modes, with one change throughout: every amount
 * here is an INTEGER in minor units. That system used PHP floats and rounded at
 * the end; a rate is money, and money that is a float is money that eventually
 * disagrees with itself by a paisa in a way nobody can reproduce.
 *
 * There is nothing to migrate into this. Every rate column in the source database
 * is zero on all 24,656 entries - `rate` and `hourly_rate` non-null but zero
 * throughout, `fixed_rate` never set once - so this is the behaviour rebuilt from
 * its source, not data carried across.
 */

/** Where a rate was found. The name is also its precedence - see resolveRate. */
export type RateSource = 'activity' | 'project' | 'customer';

/**
 * A rate as it is stored, on any of the three tables.
 *
 * `employeeId` null means it applies to everybody; set means only that person.
 */
export interface RateCandidate {
  source: RateSource;
  /** Null for a rate that applies to everybody. */
  employeeId: string | null;
  /** Minor units. Per hour, unless `isFixed`. */
  rateMinor: bigint;
  /** Minor units. Null means "fall back", which differs for fixed and hourly. */
  internalRateMinor: bigint | null;
  /** True when `rateMinor` is the whole entry rather than an hourly figure. */
  isFixed: boolean;
}

/**
 * The precedence, and it is NOT the ordering most people would guess.
 *
 * The previous system scored each candidate - activity 5, project 3, customer 1 -
 * and added ONE for a rate belonging to this specific person, then took the
 * highest. That arithmetic has a consequence worth stating out loud, because it
 * looks like a bug until you see the numbers:
 *
 *     activity, this person   6
 *     activity, everybody     5   <-- beats the next line
 *     project,  this person   4
 *     project,  everybody     3
 *     customer, this person   2
 *     customer, everybody     1
 *
 * So a GENERIC activity rate outranks a PERSONAL project rate. Specificity of the
 * thing beats specificity of the person. Kept exactly, because changing it would
 * silently rebill work that the old system priced a particular way - and if it is
 * ever to change, that has to be a decision somebody makes on purpose rather than
 * a detail lost in a port.
 */
const SOURCE_SCORE: Record<RateSource, number> = { activity: 5, project: 3, customer: 1 };

export function rateScore(candidate: RateCandidate, employeeId: string): number {
  const personal = candidate.employeeId !== null && candidate.employeeId === employeeId ? 1 : 0;
  return SOURCE_SCORE[candidate.source] + personal;
}

/**
 * The rate that applies, or null when none of the tables has one.
 *
 * A candidate belonging to a DIFFERENT person is not a candidate at all - it is
 * somebody else's rate, and scoring it would let one person's negotiated rate
 * price another person's work.
 */
export function resolveRate(
  candidates: readonly RateCandidate[],
  employeeId: string,
): RateCandidate | null {
  let best: RateCandidate | null = null;
  let bestScore = -1;
  for (const candidate of candidates) {
    if (candidate.employeeId !== null && candidate.employeeId !== employeeId) continue;
    const score = rateScore(candidate, employeeId);
    // Strictly greater, so that among equals the first wins and the result does
    // not depend on the order a query happened to return rows in.
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return best;
}

/**
 * How an hourly rate becomes a total.
 *
 * `classic` is hours as they are; `decimal` rounds the duration to two decimal
 * hours first, so 1h 0m 30s bills as 1.01 hours rather than 1.008333. Both come
 * from the previous system and both are offered there, so both are here.
 */
export type RateMode = 'classic' | 'decimal';

/** Integer division rounding half away from zero, so nothing drifts down. */
export function divRound(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n;
  const abs = negative ? -numerator : numerator;
  const result = (abs + denominator / 2n) / denominator;
  return negative ? -result : result;
}

/**
 * An hourly rate in minor units over a duration in seconds, as minor units.
 *
 * All integer arithmetic. The previous system did `hourlyRate * (seconds / 3600)`
 * in floating point and rounded to four decimal places afterwards, which is fine
 * until a rate like 1000.10 meets a duration like 4919 seconds and the answer
 * depends on the platform's last bit.
 */
export function computeRate(hourlyMinor: bigint, seconds: number, mode: RateMode): bigint {
  if (seconds <= 0 || hourlyMinor === 0n) return 0n;
  const secs = BigInt(Math.trunc(seconds));

  if (mode === 'decimal') {
    // Hundredths of an hour, rounded - the same "two decimal hours" the previous
    // system produced with round($seconds / 3600, 2).
    const hundredthsOfAnHour = divRound(secs * 100n, 3600n);
    return divRound(hourlyMinor * hundredthsOfAnHour, 100n);
  }

  return divRound(hourlyMinor * secs, 3600n);
}

export interface RateInputs {
  employeeId: string;
  /** Seconds. A zero or missing duration bills nothing. */
  seconds: number;
  /** Typed on the entry itself, and beats every table when set. */
  entryHourlyMinor?: bigint | null;
  entryFixedMinor?: bigint | null;
  /** Every rate row that touches this entry's customer, project and activity. */
  candidates: readonly RateCandidate[];
  /** The person's own defaults, used when no table and no override applies. */
  employeeHourlyMinor?: bigint | null;
  employeeInternalMinor?: bigint | null;
  mode: RateMode;
}

export interface ResolvedRate {
  /** What the entry bills, in minor units. */
  totalMinor: bigint;
  /** What it costs internally, in minor units. */
  internalMinor: bigint;
  /** The hourly figure it was computed from, or null for a fixed rate. */
  hourlyMinor: bigint | null;
  /** Set only when a fixed rate applied. */
  fixedMinor: bigint | null;
  /** Where the rate came from, for showing somebody why they are billed this. */
  source: RateSource | 'entry' | 'employee' | 'none';
}

/**
 * The whole calculation, in the previous system's order.
 *
 * Two rules in here are not obvious and both come from that system's own bug
 * history:
 *
 *  - **A fixed rate is the total, not an hourly figure.** Its internal
 *    counterpart falls back to the person's internal HOURLY rate multiplied out
 *    over the duration - not to the fixed amount - because an internal rate is
 *    always per hour. That was issue #4801 there.
 *  - **An override stops the search, it does not seed it.** When the entry itself
 *    carries an hourly rate, no table is consulted for the billed figure. The
 *    internal rate still resolves normally, because "bill this customer a flat
 *    rate" says nothing about what the work costs us.
 */
export function calculateRate(inputs: RateInputs): ResolvedRate {
  const { employeeId, seconds, candidates, mode } = inputs;
  const best = resolveRate(candidates, employeeId);

  // A fixed rate: from the entry if typed, otherwise from a table that carries one.
  const fixedMinor =
    inputs.entryFixedMinor ?? (best !== null && best.isFixed ? best.rateMinor : null);

  if (fixedMinor !== null) {
    let internalMinor: bigint;
    if (best !== null && best.isFixed && best.internalRateMinor !== null) {
      internalMinor = best.internalRateMinor;
    } else if (inputs.employeeInternalMinor != null) {
      // Per hour, so it has to be multiplied out. The fixed amount is NOT used as
      // the internal figure when the person has an internal rate of their own.
      internalMinor = computeRate(inputs.employeeInternalMinor, seconds, mode);
    } else {
      // Nothing configured: the fixed rate stands in for its own cost, which is
      // what that system does rather than reporting a zero margin.
      internalMinor = fixedMinor;
    }
    return {
      totalMinor: fixedMinor,
      internalMinor,
      hourlyMinor: null,
      fixedMinor,
      source: inputs.entryFixedMinor != null ? 'entry' : (best?.source ?? 'none'),
    };
  }

  // Hourly: the entry's own figure, else a table's, else the person's default.
  const tableHourly = best !== null && !best.isFixed ? best.rateMinor : null;
  const hourlyMinor =
    inputs.entryHourlyMinor ?? tableHourly ?? inputs.employeeHourlyMinor ?? 0n;

  const internalHourly =
    (best !== null && !best.isFixed ? best.internalRateMinor : null) ??
    inputs.employeeInternalMinor ??
    // Falls back to the billed rate, so an unconfigured internal rate reports a
    // zero margin rather than a 100% one.
    hourlyMinor;

  const source: ResolvedRate['source'] =
    inputs.entryHourlyMinor != null
      ? 'entry'
      : tableHourly !== null
        ? best!.source
        : inputs.employeeHourlyMinor != null
          ? 'employee'
          : 'none';

  return {
    totalMinor: computeRate(hourlyMinor, seconds, mode),
    internalMinor: computeRate(internalHourly, seconds, mode),
    hourlyMinor,
    fixedMinor: null,
    source,
  };
}

/* ------------------------------------------------------------- formatting -- */

/**
 * Minor units to a decimal string. Never for arithmetic - only for display and
 * for putting a value in a form field.
 */
export function minorToDecimal(minor: bigint): string {
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  const whole = abs / 100n;
  const fraction = (abs % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/**
 * A typed decimal to minor units, with string arithmetic.
 *
 * `Number(value) * 100` is exact for every amount anybody is likely to type and
 * inexact for the one they eventually do. Returns null rather than throwing, so a
 * caller can report "that is not an amount" in its own words.
 */
export function decimalToMinor(value: string): bigint | null {
  const trimmed = value.trim();
  if (!/^-?\d{1,15}(\.\d{1,2})?$/.test(trimmed)) return null;
  const negative = trimmed.startsWith('-');
  const [whole, fraction = ''] = (negative ? trimmed.slice(1) : trimmed).split('.');
  const minor = BigInt(whole || '0') * 100n + BigInt((fraction + '00').slice(0, 2));
  return negative ? -minor : minor;
}
